// Address autocomplete using Nominatim (OpenStreetMap)
import { useState, useEffect, useRef } from 'react'
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/20/solid'

interface NominatimResult {
  place_id: number
  display_name: string
  address: {
    house_number?: string
    road?: string
    suburb?: string
    city?: string
    town?: string
    village?: string
    state?: string
    postcode?: string
    country?: string
    country_code?: string
  }
}

export interface ParsedAddress {
  street: string
  city: string
  state: string
  postalCode: string
  country: string
}

interface Props {
  onSelect: (address: ParsedAddress) => void
  placeholder?: string
  className?: string
}

export function AddressAutocomplete({ onSelect, placeholder = 'Search for an address...', className = '' }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<NominatimResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounced search
  useEffect(() => {
    if (query.length < 3) {
      setResults([])
      setIsOpen(false)
      return
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true)
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
          {
            headers: {
              'Accept-Language': 'en',
            },
          }
        )
        const data: NominatimResult[] = await response.json()
        setResults(data)
        setIsOpen(data.length > 0)
        setSelectedIndex(-1)
      } catch (error) {
        console.error('Address search failed:', error)
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const parseAddress = (result: NominatimResult): ParsedAddress => {
    const addr = result.address
    const streetParts = [addr.house_number, addr.road].filter(Boolean)

    return {
      street: streetParts.join(' ') || '',
      city: addr.city || addr.town || addr.village || addr.suburb || '',
      state: addr.state || '',
      postalCode: addr.postcode || '',
      country: addr.country || '',
    }
  }

  const handleSelect = (result: NominatimResult) => {
    const parsed = parseAddress(result)
    onSelect(parsed)
    setQuery('')
    setIsOpen(false)
    setResults([])
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          {isLoading ? (
            <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="block w-full rounded-md border-gray-300 pl-9 pr-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
        />
      </div>

      {isOpen && results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {results.map((result, index) => (
            <li
              key={result.place_id}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                index === selectedIndex ? 'bg-brand-50 text-brand-900' : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-2">
                <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                <span className="block truncate">{result.display_name}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {isOpen && results.length === 0 && query.length >= 3 && !isLoading && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white py-2 px-3 text-sm text-gray-500 shadow-lg ring-1 ring-black ring-opacity-5">
          No addresses found
        </div>
      )}
    </div>
  )
}
